"use client";

import { useTranslations } from "next-intl";
import { useState } from "react";
import { InfiniteScroll } from "@/components/core/InfiniteScroll/InfiniteScroll";
import {
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui";
import { useFetchVendorProducts } from "@/hooks";
import { hasLoadedListItems } from "@/lib/pagination/hasLoadedListItems";
import { ShopItemCard } from "./ShopItemCard";

interface ShopItemsListProps {
  shopId: string;
  activity_type: string[];
}

const ShopItemCardSkeleton = () => {
  return (
    <div className="rounded-lg border border-stroke-subtle overflow-hidden p-4">
      <Skeleton variant="muted" className="size-[140px] rounded-sm" />
    </div>
  );
};

export const ShopItemsList = ({
  shopId,
  activity_type,
}: ShopItemsListProps) => {
  const t = useTranslations("shop");
  const products = useFetchVendorProducts({
    vendorId: shopId,
    limit: 10,
  });

  const hasProduct = activity_type.includes("PRODUCTS");
  const hasService = activity_type.includes("SERVICES");
  const hasBoth = hasProduct && hasService;

  const [activeTab, setActiveTab] = useState<"products" | "services">(
    "products",
  );

  // Determine title based on activity_type array (for single-type vendors)
  const getTitle = () => {
    if (hasService && !hasProduct) {
      return t("list.servicesTitle");
    }
    return t("list.productsTitle");
  };

  // Flatten all pages into a single array
  const allItems = products.data?.pages.flatMap(
    (page) => page?.data?.result || [],
  );

  // Filter items based on is_service field
  const productItems = allItems?.filter((item) => !item.is_service) ?? [];
  const serviceItems = allItems?.filter((item) => item.is_service) ?? [];

  // Get counts
  const totalCount = products.data?.pages[0]?.data?.pagination?.total ?? 0;
  const productCount = productItems.length;
  const serviceCount = serviceItems.length;

  // Show loading state (only on initial load)
  if (products.isLoading && !hasLoadedListItems(products)) {
    return (
      <div className="px-4">
        {hasBoth ? (
          <div className="mb-4">
            <Skeleton variant="muted" className="h-10 w-48" />
          </div>
        ) : (
          <h3 className="text-base font-semibold text-text-primary mb-4">
            <span className="me-1">{getTitle()}</span>
            <Skeleton variant="muted" className="inline-block h-4 w-8" />
          </h3>
        )}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 pb-4">
          {[1, 2, 3].map((id) => (
            <ShopItemCardSkeleton key={`skeleton-${id}`} />
          ))}
        </div>
      </div>
    );
  }

  // Show error state
  if (products.error && !hasLoadedListItems(products)) {
    return (
      <div className="text-center text-semantic-error py-8">
        <p>{t("list.error")}</p>
        <p className="text-sm mt-2">
          {products.error &&
          typeof products.error === "object" &&
          products.error !== null &&
          "message" in products.error
            ? String(products.error.message)
            : t("list.unknownError")}
        </p>
      </div>
    );
  }

  // Show empty state
  if (!allItems || allItems.length === 0) {
    return (
      <div className="px-4">
        {hasBoth ? (
          <Tabs
            syncWithHash={false}
            defaultValue="products"
            value={activeTab}
            onValueChange={(value) =>
              setActiveTab(value as "products" | "services")
            }
          >
            <TabsList variant="bordered" className="justify-end w-fit">
              <TabsTrigger value="products" variant="bordered">
                {t("list.products")}
              </TabsTrigger>
              <TabsTrigger value="services" variant="bordered">
                {t("list.services")}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        ) : (
          <h3 className="text-base font-semibold text-text-primary mb-4">
            <span className="me-1">{getTitle()}</span>
            <span className="text-text-disabled">({totalCount})</span>
          </h3>
        )}
        <div className="text-center text-text-secondary text-sm py-8">
          {t("list.empty")}
        </div>
      </div>
    );
  }

  // Render with tabs for vendors that have both products and services
  if (hasBoth) {
    return (
      <div>
        <div className="p-4">
          <Tabs
            syncWithHash={false}
            defaultValue="products"
            value={activeTab}
            onValueChange={(value) =>
              setActiveTab(value as "products" | "services")
            }
          >
            <TabsList variant="bordered" className="justify-end w-fit">
              <TabsTrigger value="products" variant="bordered">
                {t("list.products")}
              </TabsTrigger>
              <TabsTrigger value="services" variant="bordered">
                {t("list.services")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="products" className="mt-0">
              {productItems.length === 0 ? (
                <div className="text-center text-text-secondary text-sm py-8">
                  {t("list.productEmpty")}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 pb-4">
                  {productItems.map((item) => (
                    <ShopItemCard
                      className="border-s border-e rounded-lg overflow-hidden"
                      key={item.id}
                      {...item}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="services" className="mt-0">
              {serviceItems.length === 0 ? (
                <div className="text-center text-text-secondary text-sm py-8">
                  {t("list.serviceEmpty")}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 pb-4">
                  {serviceItems.map((item) => (
                    <ShopItemCard
                      className="border-s border-e rounded-lg overflow-hidden"
                      key={item.id}
                      {...item}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Infinite scroll trigger */}
        <InfiniteScroll query={products} className="py-4" />
      </div>
    );
  }

  // Single type: show simple title
  const displayItems = hasService ? serviceItems : productItems;
  const displayCount = hasService ? serviceCount : productCount;

  return (
    <div>
      <h3 className="text-base font-semibold text-text-primary p-4">
        <span className="me-1">{getTitle()}</span>
        <span className="text-text-disabled">
          ({displayCount || totalCount})
        </span>
      </h3>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 px-4 pb-4">
        {(displayItems.length > 0 ? displayItems : allItems).map((item) => (
          <ShopItemCard
            key={item.id}
            {...item}
            className="border-s border-e rounded-lg overflow-hidden"
          />
        ))}
      </div>

      {/* Infinite scroll trigger */}
      <InfiniteScroll query={products} className="py-4" />
    </div>
  );
};
